import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import {
  useContext,
  useEffect,
  useCallback,
  useState,
  lazy,
  Suspense,
  useMemo,
} from "react";
import { ThemeProvider } from "@mui/material/styles";
import { AuthContext } from "./context/user.context";
import { NowContext } from "./context/now.context";
import { useAppTheme } from "./context/theme.context";
import UnitSelector from "./components/unit-selector/UnitSelector";
import ProductSelector from "./components/product-selector/ProductSelector";
import LogoutDialog from "./components/navigation-bar/components/LogoutDialog";
import {
  AccessDeniedScreen,
  FirstAllowedRoute,
  RequireAppAccess,
  RequirePermission,
} from "./components/routing/RouteGuards";
import { WEB_APPS } from "./utils/routing/routePermissions";

// Importar componentes de rutas
// import { ProtectedRoute } from './components/routing/RouteHelpers';

// Importar componentes principales
import SignIn from "./components/sing-in/sign-in.component";
import DelayedLoadingScreen from "./components/DelayedLoadingScreen";

// Layout shells — imported eagerly so the navbar is always available before
// child routes resolve. Only route content is lazy-loaded.
import User from "./reseller/user.component";
import Home from "./dealership/routes/home/home.component";
import Group from "./dealership/routes/group/group.component";
import Developer from "./developers/developer.component";
import ChatBot from "./components/chatbot/ChatBot";

// Rutas públicas (sin layout ni autenticación requerida). El ChatBot no debe
// mostrarse en ellas aunque el usuario tenga sesión activa.
const PUBLIC_ROUTE_PREFIXES = [
  "/sign-in",
  "/access-denied",
  "/confirmappraisal",
  "/test-drive-feedback",
  "/loan-signing",
  "/privacidad",
];

// Componentes para Dealership (Lazy Loaded)
const NewAuction = lazy(
  () =>
    import("./dealership/components/new-auction-form/add-auction-form.component"),
);
const ModifyAuction = lazy(
  () => import("./dealership/routes/modify-auction/modify-auction.component"),
);
const ModifyDealership = lazy(
  () =>
    import("./dealership/components/modify-dealership-form/modify-dealership.component"),
);
const NewReseller = lazy(
  () =>
    import("./dealership/components/new-reseller-form/add-reseller-form.component"),
);
const NewMember = lazy(
  () =>
    import("./dealership/components/new-member-form/add-member-form.component"),
);
const MembersListPage = lazy(
  () =>
    import("./dealership/components/new-member-form/members-list-page.component"),
);
const UnitsPage = lazy(
  () => import("./dealership/components/units/units-list-page.component"),
);
const NewUnitPage = lazy(
  () => import("./dealership/components/units/new-unit-page.component"),
);
const AuctionList = lazy(
  () => import("./dealership/components/auction-list/auction-list.component"),
);
const AuctionOffers = lazy(
  () =>
    import("./dealership/components/auction-offers/auction-offers.component"),
);
const BanReseller = lazy(
  () => import("./dealership/components/ban-reseller/ban-reseller"),
);
const CarAnalyticsGroupComponent = lazy(
  () =>
    import("./dealership/components/car-analytics/car-analytics.component.jsx"),
);
const AppraisalsListRoute = lazy(
  () => import("./dealership/components/appraisal/appraisals-list.component"),
);
const AppraisalDetail = lazy(
  () =>
    import("./dealership/components/appraisal-detail/appraisal-detail.component"),
);
const AppraisalCloseRoute = lazy(
  () => import("./dealership/components/appraisal-close/appraisal-close-list"),
);
const AppraisalCfgRoute = lazy(
  () => import("./dealership/components/appraisal-cfg/appraisal-cfg.component"),
);
const AuctionsFinishedRoute = lazy(
  () =>
    import("./dealership/components/auction-finished/auction-finished-list/auction-finished-list"),
);
// Componentes para Group (Lazy Loaded)
const NewDealership = lazy(
  () =>
    import("./group/components/new-dealership-form/add-dealership-form.component"),
);
const GroupAuctionsPage = lazy(
  () => import("./group/components/group-auctions/groupAuctionsPage.jsx"),
);
const ModifyEmailsForDealershipForm = lazy(
  () =>
    import("./group/components/modify-emails-for-dealership-form/modify-emails-for-dealership-form"),
);
const AdminReports = lazy(
  () => import("./group/components/reports/reports.component"),
);
const GroupBanReseller = lazy(
  () => import("./group/components/group-ban-reseller/group-ban-reseller"),
);
const FastPurchaseRoute = lazy(
  () => import("./dealership/components/fast-purchase/fast-purchase-list"),
);
const FastPurchaseDealershipRoute = lazy(
  () =>
    import("./dealership/components/fast-purchase/fast-purchase-dealership"),
);
const GroupAnalyticsRoute = lazy(
  () =>
    import("./group/components/group-analytics/group-analytics-page.component"),
);

// Componentes para Reseller (Mobile) (Lazy Loaded)
const AuctionOverview = lazy(
  () => import("./reseller/components/auction-overview/auction-overview.jsx"),
);
const MyOffers = lazy(
  () => import("./reseller/components/my-offers/my-offers"),
);
const AuctionDetail = lazy(
  () => import("./reseller/components/auction-detail/auction-detail.jsx"),
);
const MyFavorites = lazy(
  () => import("./reseller/components/my-favorites/my-favorites"),
);
const Configuration = lazy(
  () => import("./reseller/components/configuration/configuration"),
);
const PersonalData = lazy(
  () => import("./reseller/components/personal-data/personal-data"),
);
const Terms = lazy(() => import("./reseller/components/terms/terms"));

// Componentes para Developer (Lazy Loaded)
const DataComponent = lazy(
  () => import("./developers/components/data.component"),
);
const AddGroup = lazy(
  () =>
    import("./developers/components/add-group-form/add-group-form.component"),
);
const ModifyEmailsForGroupForm = lazy(
  () =>
    import("./developers/components/modify-emails-for-group/modify-emails-for-group-form"),
);
const ResellersManagement = lazy(
  () => import("./developers/components/resellers-management/ResellersManagement"),
);
const ActiveAuctionsOffers = lazy(
  () =>
    import("./developers/components/active-auctions-offers/ActiveAuctionsOffers"),
);
const CarAnalyticsComponent = lazy(
  () => import("./developers/components/car-analytics/car-analytics.component"),
);
const AuctionsToCashOut = lazy(
  () =>
    import("./developers/components/auctions-to-cash-out/auctions-to-cash-out.component"),
);
const DemoRequests = lazy(
  () => import("./developers/components/demo-requests/DemoRequests"),
);
const MembersBackfill = lazy(
  () => import("./developers/components/members-backfill/MembersBackfill"),
);

// Headmaster: solo developers gestionan headmasters; los grupos son vista compartida.
const HeadmastersPage = lazy(
  () => import("./developers/components/headmasters/HeadmastersPage"),
);
const GroupsPage = lazy(
  () => import("./developers/components/groups/GroupsPage"),
);

// Componentes públicos (sin autenticación)
const ConfirmAppraisal = lazy(
  () => import("./components/public/ConfirmAppraisal"),
);
const TestDriveFeedback = lazy(
  () => import("./components/public/TestDriveFeedback"),
);
const LoanSigning = lazy(() => import("./components/public/LoanSigning"));
const LandingPage = lazy(() => import("./components/public/LandingPage"));
const PrivacyPolicy = lazy(
  () => import("./components/public/PrivacyPolicy"),
);

// Componentes de Test Drive y Autos (Dealership)
const CarsManagement = lazy(
  () =>
    import("./dealership/components/cars-management/cars-management.component"),
);
const TestDrivesList = lazy(
  () =>
    import("./dealership/components/test-drives/test-drives-list.component"),
);
const LoansList = lazy(
  () => import("./dealership/components/loans/loans-list.component"),
);
const FeedbackAnalytics = lazy(
  () =>
    import(
      "./dealership/components/feedback-analytics/feedback-analytics.component"
    ),
);

const App = () => {
  const { muiTheme } = useAppTheme();
  const {
    firebaseUser,
    currentUser,
    loading,
    sessionState,
    resolverError,
    logoutDialogOpen,
    confirmSignOut,
    switchUserFromLogout,
    cancelSignOutDialog,
  } = useContext(AuthContext);
  const { forceUpdateTime } = useContext(NowContext);
  const [, setAppState] = useState("active");
  const location = useLocation();

  const isPublicRoute = PUBLIC_ROUTE_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix),
  );
  const isPublicWorkflow = isPublicRoute &&
    !location.pathname.startsWith("/sign-in") &&
    !location.pathname.startsWith("/access-denied");

  const fetchArgentinaTime = useCallback(async () => {
    try {
      return await forceUpdateTime();
    } catch (error) {
      console.error("Error al obtener la hora de Argentina:", error);
    }
  }, [forceUpdateTime]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchArgentinaTime();
      }
      setAppState(document.visibilityState);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    fetchArgentinaTime();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchArgentinaTime, setAppState]);

  useEffect(() => {
    fetchArgentinaTime();
  }, [location.pathname, fetchArgentinaTime]);

  // Función para renderizar rutas según tipo de usuario
  const renderUserRoutes = useMemo(() => {
    if (!currentUser) {
      if (firebaseUser) {
        return <Route path="*" element={<AccessDeniedScreen />} />;
      }
      return (
        <>
          <Route path="/" element={<LandingPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </>
      );
    }

    switch (currentUser.activeApp) {
      case WEB_APPS.GROUP:
        return (
          <Route
            element={
              <RequireAppAccess requiredApp={WEB_APPS.GROUP}>
                <Group />
              </RequireAppAccess>
            }
          >
            <Route
              path="/"
              element={
                <RequirePermission path="/">
                  <GroupAuctionsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/new-dealership"
              element={
                <RequirePermission path="/new-dealership">
                  <NewDealership />
                </RequirePermission>
              }
            />
            <Route
              path="/modify-admins"
              element={
                <RequirePermission path="/modify-admins">
                  <ModifyEmailsForDealershipForm />
                </RequirePermission>
              }
            />
            <Route
              path="/group-ban-reseller"
              element={
                <RequirePermission path="/group-ban-reseller">
                  <GroupBanReseller />
                </RequirePermission>
              }
            />
            <Route
              path="/admin-reports"
              element={
                <RequirePermission path="/admin-reports">
                  <AdminReports />
                </RequirePermission>
              }
            />
            <Route
              path="/group-analytics"
              element={
                <RequirePermission path="/group-analytics">
                  <GroupAnalyticsRoute />
                </RequirePermission>
              }
            />
            <Route
              path="/auctions-finished"
              element={
                <RequirePermission path="/auctions-finished">
                  <AuctionsFinishedRoute />
                </RequirePermission>
              }
            />
            <Route
              path="/fast-purchase"
              element={
                <RequirePermission path="/fast-purchase">
                  <FastPurchaseRoute />
                </RequirePermission>
              }
            />
            <Route path="*" element={<FirstAllowedRoute />} />
          </Route>
        );

      case WEB_APPS.DEALERSHIP: {
        return (
          <Route
            path="/"
            element={
              <RequireAppAccess requiredApp={WEB_APPS.DEALERSHIP}>
                <Home />
              </RequireAppAccess>
            }
          >
            <Route
              index
              element={
                <RequirePermission path="/">
                  <AuctionList />
                </RequirePermission>
              }
            />
            <Route
              path="/new-auction"
              element={
                <RequirePermission path="/new-auction">
                  <NewAuction />
                </RequirePermission>
              }
            />
            <Route
              path="/new-reseller"
              element={
                <RequirePermission path="/new-reseller">
                  <NewReseller />
                </RequirePermission>
              }
            />
            <Route
              path="/new-member"
              element={
                <RequirePermission path="/new-member">
                  <NewMember />
                </RequirePermission>
              }
            />
            <Route
              path="/members"
              element={
                <RequirePermission path="/members">
                  <MembersListPage />
                </RequirePermission>
              }
            />
            <Route
              path="/units"
              element={
                <RequirePermission path="/units">
                  <UnitsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/units/new"
              element={
                <RequirePermission path="/units/new">
                  <NewUnitPage />
                </RequirePermission>
              }
            />
            <Route
              path="/modify-auction/:id"
              element={
                <RequirePermission path="/modify-auction/:id">
                  <ModifyAuction />
                </RequirePermission>
              }
            />
            <Route
              path="/modify-dealership"
              element={
                <RequirePermission path="/modify-dealership">
                  <ModifyDealership />
                </RequirePermission>
              }
            />
            <Route
              path="/auction-offers/:id"
              element={
                <RequirePermission path="/auction-offers/:id">
                  <AuctionOffers />
                </RequirePermission>
              }
            />
            <Route
              path="/ban-reseller"
              element={
                <RequirePermission path="/ban-reseller">
                  <BanReseller />
                </RequirePermission>
              }
            />
            <Route
              path="/appraisals"
              element={
                <RequirePermission path="/appraisals">
                  <AppraisalsListRoute />
                </RequirePermission>
              }
            />
            <Route
              path="/appraisals/:id"
              element={
                <RequirePermission path="/appraisals/:id">
                  <AppraisalDetail />
                </RequirePermission>
              }
            />
            <Route
              path="/appraisals-close"
              element={
                <RequirePermission path="/appraisals-close">
                  <AppraisalCloseRoute />
                </RequirePermission>
              }
            />
            <Route
              path="/appraisal-cfg"
              element={
                <RequirePermission path="/appraisal-cfg">
                  <AppraisalCfgRoute />
                </RequirePermission>
              }
            />
            <Route
              path="/auctions-finished"
              element={
                <RequirePermission path="/auctions-finished">
                  <AuctionsFinishedRoute />
                </RequirePermission>
              }
            />
            <Route
              path="/fast-purchase"
              element={
                <RequirePermission path="/fast-purchase">
                  <FastPurchaseDealershipRoute />
                </RequirePermission>
              }
            />
            <Route
              path="/vehicle-analytics"
              element={
                <RequirePermission path="/vehicle-analytics">
                  <CarAnalyticsGroupComponent />
                </RequirePermission>
              }
            />
            <Route
              path="/cars-management"
              element={
                <RequirePermission path="/cars-management">
                  <CarsManagement />
                </RequirePermission>
              }
            />
            <Route
              path="/test-drives"
              element={
                <RequirePermission path="/test-drives">
                  <TestDrivesList />
                </RequirePermission>
              }
            />
            <Route
              path="/loans"
              element={
                <RequirePermission path="/loans">
                  <LoansList />
                </RequirePermission>
              }
            />
            <Route
              path="/feedback-analytics"
              element={
                <RequirePermission path="/feedback-analytics">
                  <FeedbackAnalytics />
                </RequirePermission>
              }
            />
            <Route path="*" element={<FirstAllowedRoute />} />
          </Route>
        );
      }

      case WEB_APPS.RESELLER:
        return (
          <Route
            path="/"
            element={
              <RequireAppAccess requiredApp={WEB_APPS.RESELLER}>
                <User />
              </RequireAppAccess>
            }
          >
            <Route
              path="/"
              element={
                <RequirePermission path="/">
                  <AuctionOverview />
                </RequirePermission>
              }
            />
            <Route
              path="/my-offers"
              element={
                <RequirePermission path="/my-offers">
                  <MyOffers />
                </RequirePermission>
              }
            />
            <Route
              path="/auction/:id"
              element={
                <RequirePermission path="/auction/:id">
                  <AuctionDetail />
                </RequirePermission>
              }
            />
            <Route
              path="/my-favorites"
              element={
                <RequirePermission path="/my-favorites">
                  <MyFavorites />
                </RequirePermission>
              }
            />
            <Route
              path="/configuration"
              element={
                <RequirePermission path="/configuration">
                  <Configuration />
                </RequirePermission>
              }
            />
            <Route
              path="/personal-data"
              element={
                <RequirePermission path="/personal-data">
                  <PersonalData />
                </RequirePermission>
              }
            />
            <Route
              path="/terms"
              element={
                <RequirePermission path="/terms">
                  <Terms />
                </RequirePermission>
              }
            />
            <Route path="*" element={<FirstAllowedRoute />} />
          </Route>
        );

      case WEB_APPS.DEVELOPER:
        return (
          <Route
            path="/"
            element={
              <RequireAppAccess requiredApp={WEB_APPS.DEVELOPER}>
                <Developer />
              </RequireAppAccess>
            }
          >
            <Route
              index
              element={
                <RequirePermission path="/">
                  <DataComponent />
                </RequirePermission>
              }
            />
            <Route
              path="/demo-requests"
              element={
                <RequirePermission path="/demo-requests">
                  <DemoRequests />
                </RequirePermission>
              }
            />
            <Route
              path="/new-group"
              element={
                <RequirePermission path="/new-group">
                  <AddGroup />
                </RequirePermission>
              }
            />
            <Route
              path="/modify-members"
              element={
                <RequirePermission path="/modify-members">
                  <ModifyEmailsForGroupForm />
                </RequirePermission>
              }
            />
            <Route
              path="/car-analytics"
              element={
                <RequirePermission path="/car-analytics">
                  <CarAnalyticsComponent />
                </RequirePermission>
              }
            />
                        <Route
              path="/resellers-management"
              element={
                <RequirePermission path="/resellers-management">
                  <ResellersManagement />
                </RequirePermission>
              }
            />
<Route
              path="/active-auction-offers"
              element={
                <RequirePermission path="/active-auction-offers">
                  <ActiveAuctionsOffers />
                </RequirePermission>
              }
            />
            <Route
              path="/auctions-to-cash-out"
              element={
                <RequirePermission path="/auctions-to-cash-out">
                  <AuctionsToCashOut />
                </RequirePermission>
              }
            />
            <Route
              path="/headmasters"
              element={
                <RequirePermission path="/headmasters">
                  <HeadmastersPage />
                </RequirePermission>
              }
            />
            <Route
              path="/groups"
              element={
                <RequirePermission path="/groups">
                  <GroupsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/members-backfill"
              element={
                <RequirePermission path="/members-backfill">
                  <MembersBackfill />
                </RequirePermission>
              }
            />
            <Route path="*" element={<FirstAllowedRoute />} />
          </Route>
        );

      default:
        return <Route path="*" element={<Navigate to="/sign-in" replace />} />;
    }
  }, [currentUser, firebaseUser]);

  // Mostrar loading mientras se verifica la autenticación
  if (!isPublicWorkflow && (loading || (firebaseUser && sessionState === "resolving"))) {
    return (
      <ThemeProvider theme={muiTheme}>
        <DelayedLoadingScreen />
        <LogoutDialog
          open={logoutDialogOpen}
          onClose={cancelSignOutDialog}
          onSwitchUser={switchUserFromLogout}
          onSignOut={confirmSignOut}
        />
      </ThemeProvider>
    );
  }

  // WEB-004: usuario multisedes debe elegir unidad antes de entrar.
  if (!isPublicWorkflow && sessionState === "selecting") {
    return (
      <ThemeProvider theme={muiTheme}>
        <UnitSelector />
        <LogoutDialog
          open={logoutDialogOpen}
          onClose={cancelSignOutDialog}
          onSwitchUser={switchUserFromLogout}
          onSignOut={confirmSignOut}
        />
      </ThemeProvider>
    );
  }

  // Usuario con acceso a más de un producto elige dónde ingresar.
  if (!isPublicWorkflow && sessionState === "selecting_product") {
    return (
      <ThemeProvider theme={muiTheme}>
        <ProductSelector />
        <LogoutDialog
          open={logoutDialogOpen}
          onClose={cancelSignOutDialog}
          onSwitchUser={switchUserFromLogout}
          onSignOut={confirmSignOut}
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <Suspense fallback={<DelayedLoadingScreen />}>
        <Routes>
          {/* Ruta pública para login - redirige si ya hay sesión */}
          <Route
            path="/sign-in"
            element={
              firebaseUser ? (
                sessionState === "ready" ? (
                  <FirstAllowedRoute />
                ) : (
                  <AccessDeniedScreen message={resolverError} />
                )
              ) : (
                <SignIn />
              )
            }
          />

          <Route
            path="/access-denied"
            element={<AccessDeniedScreen message={resolverError} />}
          />

          {/* Ruta pública para confirmación de peritaje */}
          <Route
            path="/confirmappraisal/:appraisalId"
            element={<ConfirmAppraisal />}
          />

          {/* Ruta pública para feedback de test drive */}
          <Route
            path="/test-drive-feedback/:testDriveId"
            element={<TestDriveFeedback />}
          />

          {/* Ruta pública para firma de préstamo (sin auth, token opaco) */}
          <Route path="/loan-signing/:loanId" element={<LoanSigning />} />

          {/* Política pública usada por las fichas de App Store y Play Store. */}
          <Route path="/privacidad" element={<PrivacyPolicy />} />

          {/* Rutas protegidas por aplicación y permisos explícitos. */}
          {renderUserRoutes}
        </Routes>
        {currentUser && sessionState === "ready" && !isPublicRoute && (
          <ChatBot />
        )}
        <LogoutDialog
          open={logoutDialogOpen}
          onClose={cancelSignOutDialog}
          onSwitchUser={switchUserFromLogout}
          onSignOut={confirmSignOut}
        />
      </Suspense>
    </ThemeProvider>
  );
};

export default App;
